// Domain types — single source of truth in @expat/shared-types
import type {
    Country,
    Currency,
    CashFlowType,
    ResidencyEntry,
    AccountType,
    AssetAccount,
    CashFlowCategory,
    DynamicCashFlow,
    GoalCategory,
    Goal,
    GoalEvent,
    GoalFundingSummary,
    YearlyDataPoint,
    WithdrawalTaxCategory,
    DrawdownSequenceEntry,
    WithdrawalSummary,
    HouseholdMembership,
    HouseholdMetadata,
} from '@expat/shared-types';

export type {
    Country,
    Currency,
    CashFlowType,
    ResidencyEntry,
    AccountType,
    AssetAccount,
    CashFlowCategory,
    DynamicCashFlow,
    GoalCategory,
    Goal,
    GoalEvent,
    GoalFundingSummary,
    YearlyDataPoint,
    WithdrawalTaxCategory,
    DrawdownSequenceEntry,
    WithdrawalSummary,
    HouseholdMembership,
    HouseholdMetadata,
};

// UI-specific: extends the stored profile with a computed currentAge from the API response
export interface UserProfile {
    cognitoSub: string;
    birthYear?: number;
    currentAge: number;
    targetRetirementAge: number;
    baseCurrency: Currency;
    residencyHistory: Array<{ startAge: number; endAge: number; country: Country }>;
    displayName: string;
    createdAt: string;
    updatedAt: string;
}

// UI-specific: composed API response type
export interface HouseholdResponse {
    membership: { householdId: string; role: 'OWNER' | 'MEMBER'; joinedAt: string };
    metadata: { name: string; createdAt: string; ownerSub: string };
}

// UI display constants
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
    NRE: 'NRE Account',
    NRO: 'NRO Account',
    EQUITY_IN: 'Indian Equity',
    MF_IN: 'Indian Mutual Funds',
    REAL_ESTATE: 'Real Estate (India)',
    PPF: 'PPF',
    NPS: 'NPS',
    '401K': '401(k)',
    IRA_ROTH: 'Roth IRA',
    IRA_TRADITIONAL: 'Traditional IRA',
    TAXABLE_US: 'US Taxable Brokerage',
    REAL_ESTATE_US: 'Real Estate (US)',
    VEHICLE_US: 'Vehicle (US)',
    TFSA: 'TFSA',
    RRSP: 'RRSP',
    TAXABLE_CAN: 'Canada Taxable Brokerage',
    REAL_ESTATE_CAN: 'Real Estate (Canada)',
    VEHICLE_CAN: 'Vehicle (Canada)',
};

export const COUNTRY_ACCOUNT_TYPES: Record<Country, AccountType[]> = {
    INDIA: ['NRE', 'NRO', 'EQUITY_IN', 'MF_IN', 'REAL_ESTATE', 'PPF', 'NPS'],
    US: ['401K', 'IRA_ROTH', 'IRA_TRADITIONAL', 'TAXABLE_US', 'REAL_ESTATE_US', 'VEHICLE_US'],
    CAN: ['TFSA', 'RRSP', 'TAXABLE_CAN', 'REAL_ESTATE_CAN', 'VEHICLE_CAN'],
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
    INR: '₹',
    USD: '$',
    CAD: 'C$',
};

export const COUNTRY_CURRENCIES: Record<Country, Currency> = {
    INDIA: 'INR',
    US: 'USD',
    CAN: 'CAD',
};

export const CASHFLOW_CATEGORY_LABELS: Record<CashFlowCategory, string> = {
    SALARY: 'Salary',
    RENTAL: 'Rental Income',
    DIVIDEND: 'Dividends',
    PENSION: 'Pension',
    HEALTHCARE_BRIDGE: 'Healthcare Bridge',
    LIVING_COST: 'Living Costs',
    EDUCATION: 'Education',
    VACATION: 'Vacation',
    HOME_PURCHASE: 'Home Purchase',
    VEHICLE_PURCHASE: 'Vehicle Purchase',
    INSURANCE: 'Insurance',
};

export const INCOME_CATEGORIES: CashFlowCategory[] = ['SALARY', 'RENTAL', 'DIVIDEND', 'PENSION'];
export const EXPENSE_CATEGORIES: CashFlowCategory[] = [
    'HEALTHCARE_BRIDGE',
    'LIVING_COST',
    'EDUCATION',
    'VACATION',
    'HOME_PURCHASE',
    'VEHICLE_PURCHASE',
    'INSURANCE',
];
export const RETIREMENT_EXPENSE_CATEGORIES: CashFlowCategory[] = [
    'LIVING_COST',
    'EDUCATION',
    'VACATION',
    'HOME_PURCHASE',
    'VEHICLE_PURCHASE',
    'INSURANCE',
    'HEALTHCARE_BRIDGE',
];

export const GOAL_CATEGORY_LABELS: Record<GoalCategory, string> = {
    REAL_ESTATE: 'Real Estate',
    EDUCATION: 'Education',
    WEDDING: 'Wedding',
    TRAVEL: 'Travel',
    VEHICLE: 'Vehicle',
    OTHER: 'Other',
};

// Tax analysis types (UI/API-specific, not stored in DynamoDB)
export interface TaxAssetBreakdown {
    label: string;
    accountType: string;
    country: Country;
    annualGain: number;
    annualTax: number;
    effectiveRate: number;
    taxType: string;
}

export interface TaxProjectionPoint {
    age: number;
    year: number;
    india: number;
    us: number;
    can: number;
}

export interface IndiaTaxBreakdown {
    nroInterestIncome: number;
    nroTds: number;
    rentalIncome: number;
    rentalTds: number;
    ltcgIncome: number;
    ltcgTax: number;
    totalIndiaTax: number;
    dtaaCreditsAvailable: number;
}

export interface TaxActionItem {
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
    category: string;
    title: string;
    description: string;
}

export interface TaxAnalysisResult {
    baseCurrency: Currency;
    currentAge: number;
    currentYearTax: { india: number; us: number; can: number; total: number };
    effectiveTaxRate: number;
    taxByAssetClass: TaxAssetBreakdown[];
    projectedAnnualTax: TaxProjectionPoint[];
    indiaTaxBreakdown: IndiaTaxBreakdown;
    nroTdsWarning: boolean;
    dtaaAlert: boolean;
    pficTrapDetected: boolean;
    pficAccountLabels: string[];
    departureTaxRisk: boolean;
    departureTaxEstimateCAD: number;
    rothConversionOpportunity: boolean;
    actionItems: TaxActionItem[];
}

// Projection result shape returned by the API
export interface ProjectionResult {
    successRate: number;
    safeAnnualSpend: number;
    baseCurrency: Currency;
    retirementAge: number;
    startingWealth: number;
    yearlyDataPoints: YearlyDataPoint[];
    goalFundingSummary?: GoalFundingSummary[];
    withdrawalSummary?: WithdrawalSummary;
}

export interface SavedScenario {
    id: string;
    name: string;
    savedAt: string;
    inputs: {
        retirementAge: number;
        retirementCountry: Country;
        withdrawalAmount?: number;
    };
    results: {
        successRate: number;
        safeAnnualSpend: number;
        startingWealth: number;
        retirementAge: number;
        baseCurrency: Currency;
    };
}
